import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import { useAuth } from '@context/AuthContext'
import PageHeader from '@components/common/PageHeader'
import MarkdownRenderer from '@components/docs/MarkdownRenderer'
import {
  DEFAULT_DOC_SLUG,
  DOCS_BY_SLUG,
  DOC_GROUPS,
  roleSlugForName,
} from './docsConfig'

/**
 * In-app Help / Documentation center. Open to every authenticated user (no
 * permission gate). Left-nav + content pane on desktop; a searchable picker on
 * mobile. Surfaces the role guide matching the current user's primary role.
 */
export default function DocsPage() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const { user } = useAuth()
  const params = useParams()

  const slug = params['*'] || DEFAULT_DOC_SLUG
  const body = DOCS_BY_SLUG[slug]

  // "Your role" shortcut: map the user's primary role name to its guide.
  const primaryRole = user?.profile?.roles?.[0]
  const yourRoleSlug = primaryRole ? roleSlugForName(primaryRole.name) : null

  const pickerOptions = useMemo(
    () =>
      DOC_GROUPS.flatMap((g) => g.items.map((it) => ({ ...it, group: g.group }))),
    []
  )

  const go = (s) => navigate(`/docs/${s}`)

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Help & Docs"
        subtitle="Guides for every area and role in DuoNep"
      />

      {yourRoleSlug && (
        <Alert
          icon={<MenuBookIcon fontSize="inherit" />}
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Chip
              label="Open guide"
              size="small"
              color="primary"
              onClick={() => go(yourRoleSlug)}
              clickable
            />
          }
        >
          Your role: <strong>{primaryRole.name}</strong> — read what you can do here.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        {/* Desktop left-nav */}
        {!isMobile && (
          <Paper
            variant="outlined"
            sx={{
              width: 260,
              flexShrink: 0,
              position: 'sticky',
              top: 88,
              maxHeight: 'calc(100vh - 120px)',
              overflowY: 'auto',
              borderRadius: 2,
            }}
          >
            <List dense disablePadding>
              {DOC_GROUPS.map((group) => (
                <li key={group.group}>
                  <ul style={{ padding: 0 }}>
                    <ListSubheader
                      disableSticky
                      sx={{ fontWeight: 700, lineHeight: '32px' }}
                    >
                      {group.group}
                    </ListSubheader>
                    {group.items.map((it) => (
                      <ListItemButton
                        key={it.slug}
                        selected={it.slug === slug}
                        onClick={() => go(it.slug)}
                        sx={{ pl: 3 }}
                      >
                        <ListItemText
                          primary={it.title}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItemButton>
                    ))}
                    <Divider />
                  </ul>
                </li>
              ))}
            </List>
          </Paper>
        )}

        {/* Content pane */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {/* Mobile picker */}
          {isMobile && (
            <Autocomplete
              options={pickerOptions}
              groupBy={(o) => o.group}
              getOptionLabel={(o) => o.title || ''}
              isOptionEqualToValue={(o, v) => o.slug === v.slug}
              value={pickerOptions.find((o) => o.slug === slug) || null}
              onChange={(_, v) => v && go(v.slug)}
              disableClearable
              renderInput={(p) => (
                <TextField {...p} label="Browse docs" size="small" fullWidth />
              )}
              sx={{ mb: 2 }}
            />
          )}

          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
            {body ? (
              <MarkdownRenderer>{body}</MarkdownRenderer>
            ) : (
              <Box>
                <Typography variant="h5" gutterBottom>
                  Not found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We couldn&apos;t find the page &quot;{slug}&quot;. Pick a topic
                  from the list.
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  )
}
